import {
  ArrowRightIcon,
  Button,
  ChevronRightIcon,
  Skeleton,
} from '@buffer-mono/popcorn'

import { ContentCard } from './ContentCard'
import type { ContentCardData } from './ContentCard'
import { useRecommendedContent } from './useRecommendedContent'

import { SectionHeader } from '../SectionHeader'

import { CtaButtonWrapper } from '~publish/components/CtaButtonWrapper/CtaButtonWrapper'
import { getSlotIds } from '~publish/helpers/getSlotIds'
import { useCta } from '~publish/hooks/useCta'

import styles from './ContentCarousel.module.css'

import type { JSX } from 'react'

export type ContentItem = ContentCardData & {
  /** Stable identifier used as the React key. */
  id: string
  /** Destination the card links to when clicked. */
  url: string
}

const MAX_ITEMS = 4
const BLOG_URL = 'https://buffer.com/resources/'

export function ContentCarousel(): JSX.Element | null {
  const { items, loading } = useRecommendedContent()
  const { clicked: trackArticleClicked } = useCta(
    'publish-home-blogCarousel-article-1',
  )
  const visibleItems = items.slice(0, MAX_ITEMS)

  if (!loading && visibleItems.length === 0) {
    return null
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <SectionHeader>From the Blog</SectionHeader>
        <CtaButtonWrapper cta="publish-home-blogCarousel-viewAllArticles-1">
          <Button
            as="a"
            href={BLOG_URL}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant="tertiary"
            className={styles.seeAll}
          >
            View All Articles
            <ChevronRightIcon />
          </Button>
        </CtaButtonWrapper>
      </div>
      {loading ? (
        <div
          className={styles.grid}
          // Skeleton visibility is announced as live status, not form output.
          // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
          role="status"
          aria-label="Loading content"
        >
          {getSlotIds('content-card-skeleton', MAX_ITEMS).map((skeletonId) => (
            <div
              key={skeletonId}
              className={styles.skeletonCard}
              data-testid="skeleton-placeholder"
            >
              <Skeleton className={styles.skeletonThumbnail} />
              <div className={styles.skeletonHeader}>
                <Skeleton className={styles.skeletonMeta} />
                <Skeleton className={styles.skeletonTitle} />
                <Skeleton className={styles.skeletonAction} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.grid}>
          {visibleItems.map((item) => (
            <a
              key={item.id}
              href={item.url}
              target="_blank"
              rel="noreferrer"
              aria-label={item.title}
              className={styles.cardLink}
              onClick={trackArticleClicked}
            >
              <ContentCard content={item} className={styles.card}>
                <div className={styles.cardContent}>
                  <ContentCard.Thumbnail />
                  <div className={styles.cardHeader}>
                    <div className={styles.cardTopRow}>
                      <ContentCard.Category />
                      <ContentCard.PublishedAt />
                    </div>
                    <ContentCard.Title
                      maxLines={2}
                      className={styles.cardTitle}
                    />
                    <ArrowRightIcon
                      className={styles.cardIcon}
                      size={20}
                      aria-hidden
                    />
                  </div>
                </div>
              </ContentCard>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
